import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  AddressValidationResult,
  AddressValidationVerdict,
  ValidateBillingAddressDoc,
} from '@minecraft.graphql-operations';
import { captureExceptionEvent } from '@blocs.observability';
import { BillingAddressFields, toAddressValidationInput } from './formatters';

export const ACCEPTED_RESULT: AddressValidationResult = {
  verdict: AddressValidationVerdict.Accept,
  fields: [],
  failedOpen: false,
};
export const FAILED_OPEN_RESULT: AddressValidationResult = {
  verdict: AddressValidationVerdict.Accept,
  fields: [],
  failedOpen: true,
};

export type CheckBillingAddressResult = {
  result: AddressValidationResult;
  // True only when this call actually received and parsed a verdict from Google — false for the
  // non-US skip and the network-failure/malformed-response fail-open. A clean domestic ACCEPT and the
  // non-US skip both produce an identical AddressValidationResult (empty fields, failedOpen: false),
  // so this flag is the only way a caller can tell "Google was actually asked" from "never asked".
  wasCheckedByGoogle: boolean;
};

export const useCheckBillingAddress = () => {
  const client = useApolloClient();

  return useCallback(
    async (fields: BillingAddressFields): Promise<CheckBillingAddressResult> => {
      if (fields.country !== 'US') {
        return { result: ACCEPTED_RESULT, wasCheckedByGoogle: false };
      }
      try {
        const { data } = await client.query({
          query: ValidateBillingAddressDoc,
          variables: { input: toAddressValidationInput(fields) },
          fetchPolicy: 'network-only',
        });
        if (!data?.validateBillingAddress) {
          return { result: FAILED_OPEN_RESULT, wasCheckedByGoogle: false };
        }
        return { result: data.validateBillingAddress, wasCheckedByGoogle: true };
      } catch (error) {
        captureExceptionEvent(error, { location: 'useCheckBillingAddress' });
        return { result: FAILED_OPEN_RESULT, wasCheckedByGoogle: false };
      }
    },
    [client]
  );
};
